import ReportsPreferencesService from './reports-preferences.ts';
import {
  queryUserPreferenceSettingsRequest,
  upsertUserPreferenceSettingRequest,
} from 'tio-data/requests/user-preference-settings';

/** The part of a returned row the pins read. */
interface PreferenceRow {
  id: string;
  attributes?: { value?: Record<string, unknown> };
}

/** Kept apart from the staff subject so a TIO admin's pins and a partner's never share a row. */
export const PARTNER_PINNED_REPORTS_SUBJECT = 'partner-admin:reports';

/** A single lowercase word, because the endpoint dasherizes every `value` key it returns. */
const PINNED_IDS_KEY = 'ids';

/**
 * A partner admin's pinned reports — the reports-preferences logic (cap, optimistic
 * toggle, per-owner scoping, silent degrade on 403) stored in the caller's own
 * user-preference rows instead of the staff-only `/settings` surface. Resolved as
 * `service:partner-reports-preferences`.
 */
export default class PartnerReportsPreferencesService extends ReportsPreferencesService {
  protected async readPins(): Promise<{ settingId: string | null; ids: unknown }> {
    const { content } = await this.store.request(
      queryUserPreferenceSettingsRequest({
        subject: PARTNER_PINNED_REPORTS_SUBJECT,
        predicate: this.pinnedPredicate,
      })
    );
    const row = (content as { data?: PreferenceRow[] })?.data?.[0];
    return { settingId: row?.id ?? null, ids: row?.attributes?.value?.[PINNED_IDS_KEY] };
  }

  protected async writePins(_ownerId: string, reportIds: string[]): Promise<string | null> {
    const { content } = await this.store.request(
      upsertUserPreferenceSettingRequest({
        subject: PARTNER_PINNED_REPORTS_SUBJECT,
        predicate: this.pinnedPredicate,
        value: { [PINNED_IDS_KEY]: reportIds },
      })
    );
    return (content as { data?: PreferenceRow })?.data?.id ?? null;
  }
}
